import { Inbox, Plus, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';

import { Switch } from '~/components/form';
import { aiStatus } from '~/components/pages/ai/common/statusMaps';
import { AddModelsModal } from '~/components/pages/ai/settings/AddModelsModal';
import {
  Alert,
  Button,
  Card,
  CardBody,
  ConfirmModal,
  EmptyState,
  Modal,
  ModalActions,
  PanelTable,
  type PanelTableColumn,
  RowActionsMenu,
  StatusChip,
} from '~/components/ui';
import { ListToolbar } from '~/components/layout';
import { useListUrlState } from '~/hooks/useListUrlState';
import type { AddModelsSummary } from '~/lib/ai/addModelsResult';
import { matchesProviderModelSearch } from '~/lib/ai/listFilters';
import { apiErrorMessage, type ApiErrorInput } from '~/lib/apiError';
import {
  defaultProviderCostDraft,
  extractProviderModelInUse,
  formatProviderCost,
  isProviderCostInput,
  parseProviderCostInput,
  providerCostDirtyKey,
  providerCostDraftValue,
  providerUsageRefLabel,
  syncProviderCostDrafts,
  type ProviderCostDraft,
  type ProviderCostField,
  type ProviderModelInUse,
  type ProviderModelUsageRef,
} from '~/lib/ai/settingsForm';
import {
  useGetConfiguredModelsQuery,
  useRemoveConfiguredModelMutation,
  useUpdateProviderModelMutation,
} from '~/modules/query/ai/providers';
import type { ProviderModel } from '~/modules/query/ai/types';

const COST_FIELD_LABEL: Record<ProviderCostField, string> = {
  inputCostPer1M: 'Input',
  outputCostPer1M: 'Output',
};

interface InUseNotice extends ProviderModelInUse {
  action: 'deactivate' | 'remove';
  modelName: string;
}

interface UsageGroupProps {
  label: string;
  refs: ProviderModelUsageRef[];
}

function UsageGroup({ label, refs }: UsageGroupProps) {
  if (refs.length === 0) {
    return null;
  }

  return (
    <div>
      <p className="text-sm font-medium text-gray-700 dark:text-gray-300">{label}</p>
      <ul className="mt-1 list-disc space-y-1 pl-5 text-sm text-gray-500 dark:text-gray-400">
        {refs.map((ref) => (
          <li key={`${label}-${ref.id}`}>{providerUsageRefLabel(ref)}</li>
        ))}
      </ul>
    </div>
  );
}

interface CostCellProps {
  field: ProviderCostField;
  model: ProviderModel;
  value: string;
  disabled: boolean;
  error?: string;
  onChange: (value: string) => void;
  onCommit: () => void;
}

/** A cost input inside a table cell: the column header names the field, the cell carries its own `$`. */
function CostCell({ field, model, value, disabled, error, onChange, onCommit }: CostCellProps) {
  const id = `ai-settings-provider-${model.id}-${field === 'inputCostPer1M' ? 'input' : 'output'}-cost`;

  return (
    <div className="w-36">
      <div className="flex items-center gap-1">
        <span className="text-gray-500 dark:text-gray-400" aria-hidden="true">
          $
        </span>
        <input
          id={id}
          type="text"
          inputMode="decimal"
          className="field-control min-w-0 flex-1"
          aria-label={`${COST_FIELD_LABEL[field]} cost per 1 million tokens for ${model.name} in US dollars`}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? `${id}-error` : undefined}
          value={value}
          disabled={disabled}
          placeholder="0.00"
          onChange={(event) => onChange(event.target.value)}
          onBlur={onCommit}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.currentTarget.blur();
            }
          }}
          data-testid={id}
        />
      </div>
      {error ? (
        <p id={`${id}-error`} role="alert" className="field-error">
          {error}
        </p>
      ) : null}
    </div>
  );
}

/**
 * The configured provider models: add or remove one, set availability and
 * per-token pricing. Each row saves on the control itself
 * (the switch immediately, a cost on blur or Enter) rather than through a card
 * footer: the table is the form.
 */
export default function ModelsSection() {
  const list = useListUrlState();
  const q = list.q;
  const { data, error, isLoading, refetch } = useGetConfiguredModelsQuery();
  const [updateProviderModel] = useUpdateProviderModelMutation();
  const [removeModel] = useRemoveConfiguredModelMutation();
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState<ProviderModel | null>(null);
  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const [costDrafts, setCostDrafts] = useState<Record<string, ProviderCostDraft>>({});
  const [pendingModelIds, setPendingModelIds] = useState<Record<string, boolean>>({});
  const [costErrors, setCostErrors] = useState<Record<string, string>>({});
  const [inUse, setInUse] = useState<InUseNotice | null>(null);
  const dirtyFieldKeysRef = useRef<Set<string>>(new Set());

  const models = data?.data;

  useEffect(() => {
    if (!models) {
      return;
    }

    setCostDrafts((previous) =>
      syncProviderCostDrafts(previous, dirtyFieldKeysRef.current, models),
    );
  }, [models]);

  const rows = useMemo(
    () => (models ?? []).filter((model) => matchesProviderModelSearch(model, q)),
    [models, q],
  );

  const isEmpty = !isLoading && !error && models !== undefined && models.length === 0 && !q;

  const setModelPending = (modelId: string, pending: boolean) => {
    setPendingModelIds((previous) => {
      if (pending) {
        return { ...previous, [modelId]: true };
      }

      const next = { ...previous };
      delete next[modelId];
      return next;
    });
  };

  const clearCostError = (key: string) => {
    setCostErrors((previous) => {
      if (!(key in previous)) {
        return previous;
      }

      const next = { ...previous };
      delete next[key];
      return next;
    });
  };

  const toggleActive = async (model: ProviderModel) => {
    setModelPending(model.id, true);

    try {
      await updateProviderModel({ modelId: model.id, isActive: !model.isActive }).unwrap();
      toast.success(`${model.name} is now ${model.isActive ? 'inactive' : 'active'}`);
    } catch (toggleError) {
      const notice = extractProviderModelInUse(toggleError);

      if (notice) {
        setInUse({ ...notice, action: 'deactivate', modelName: model.name });
      } else {
        toast.error(
          apiErrorMessage(toggleError as ApiErrorInput) ??
            `The availability of ${model.name} could not be changed. Try again.`,
        );
      }
    } finally {
      setModelPending(model.id, false);
    }
  };

  const finishAdd = (summary: AddModelsSummary) => {
    if (summary.tone === 'success') {
      setAlertMessage(null);
      toast.success(summary.message);
    } else {
      setAlertMessage(summary.message);
    }
  };

  const confirmRemove = async () => {
    const model = removing;

    if (!model) {
      return;
    }

    setModelPending(model.id, true);

    try {
      await removeModel(model.id).unwrap();
      setRemoving(null);
      setAlertMessage(null);
      toast.success(`${model.name} removed`);
    } catch (removeError) {
      setRemoving(null);
      const notice = extractProviderModelInUse(removeError);

      if (notice) {
        setInUse({ ...notice, action: 'remove', modelName: model.name });
      } else {
        setAlertMessage(
          apiErrorMessage(removeError as ApiErrorInput) ??
            `${model.name} could not be removed. Try again.`,
        );
      }
    } finally {
      setModelPending(model.id, false);
    }
  };

  const changeCost = (modelId: string, field: ProviderCostField, value: string) => {
    if (!isProviderCostInput(value)) {
      return;
    }

    dirtyFieldKeysRef.current.add(providerCostDirtyKey(modelId, field));
    clearCostError(providerCostDirtyKey(modelId, field));
    setCostDrafts((previous) => {
      const current = previous[modelId] ?? defaultProviderCostDraft();

      return {
        ...previous,
        [modelId]: {
          ...current,
          ...(field === 'inputCostPer1M'
            ? { inputCostPerMillion: value }
            : { outputCostPerMillion: value }),
        },
      };
    });
  };

  const commitCost = async (model: ProviderModel, field: ProviderCostField) => {
    const key = providerCostDirtyKey(model.id, field);
    const parsed = parseProviderCostInput(providerCostDraftValue(costDrafts[model.id], field));

    if (!parsed.ok) {
      setCostErrors((previous) => ({
        ...previous,
        [key]: `${COST_FIELD_LABEL[field]} cost must be zero or more.`,
      }));
      return;
    }

    const serverValue = field === 'inputCostPer1M' ? model.inputCostPer1M : model.outputCostPer1M;

    if (parsed.value === serverValue) {
      dirtyFieldKeysRef.current.delete(key);
      clearCostError(key);
      return;
    }

    setModelPending(model.id, true);

    try {
      await updateProviderModel(
        field === 'inputCostPer1M'
          ? { modelId: model.id, inputCostPer1M: parsed.value }
          : { modelId: model.id, outputCostPer1M: parsed.value },
      ).unwrap();
      dirtyFieldKeysRef.current.delete(key);
      clearCostError(key);
      toast.success(`${COST_FIELD_LABEL[field]} cost updated for ${model.name}`);
    } catch (costError) {
      toast.error(
        apiErrorMessage(costError as ApiErrorInput) ??
          `The ${COST_FIELD_LABEL[field].toLowerCase()} cost for ${model.name} could not be saved. Try again.`,
      );
    } finally {
      setModelPending(model.id, false);
    }
  };

  const columns: PanelTableColumn<ProviderModel>[] = [
    {
      header: 'Model',
      width: 'w-80',
      render: (model) => (
        <div>
          <p className="font-medium text-gray-800 dark:text-white/90">{model.name}</p>
          <p className="font-mono text-xs text-gray-500 dark:text-gray-400">{model.id}</p>
        </div>
      ),
    },
    {
      header: 'Owner',
      width: 'w-40',
      render: (model) => model.ownedBy || '—',
    },
    {
      header: 'Active',
      width: 'w-48',
      render: (model) => {
        const pending = Boolean(pendingModelIds[model.id]);

        return (
          <div className="flex items-center gap-3">
            <Switch
              id={`ai-settings-provider-${model.id}-active`}
              checked={model.isActive}
              disabled={pending}
              ariaLabel={`Make ${model.name} ${model.isActive ? 'inactive' : 'active'}`}
              onChange={() => {
                void toggleActive(model);
              }}
              testId={`ai-settings-provider-${model.id}-active`}
            />
            <StatusChip {...aiStatus(model.isActive ? 'active' : 'inactive')} />
          </div>
        );
      },
    },
    {
      header: 'Input cost / 1M tokens',
      width: 'w-44',
      render: (model) => (
        <CostCell
          field="inputCostPer1M"
          model={model}
          value={
            costDrafts[model.id]?.inputCostPerMillion ?? formatProviderCost(model.inputCostPer1M)
          }
          disabled={Boolean(pendingModelIds[model.id])}
          error={costErrors[providerCostDirtyKey(model.id, 'inputCostPer1M')]}
          onChange={(value) => changeCost(model.id, 'inputCostPer1M', value)}
          onCommit={() => {
            void commitCost(model, 'inputCostPer1M');
          }}
        />
      ),
    },
    {
      header: 'Output cost / 1M tokens',
      width: 'w-44',
      render: (model) => (
        <CostCell
          field="outputCostPer1M"
          model={model}
          value={
            costDrafts[model.id]?.outputCostPerMillion ?? formatProviderCost(model.outputCostPer1M)
          }
          disabled={Boolean(pendingModelIds[model.id])}
          error={costErrors[providerCostDirtyKey(model.id, 'outputCostPer1M')]}
          onChange={(value) => changeCost(model.id, 'outputCostPer1M', value)}
          onCommit={() => {
            void commitCost(model, 'outputCostPer1M');
          }}
        />
      ),
    },
    {
      header: 'Actions',
      width: 'w-24',
      render: (model) => (
        <RowActionsMenu
          testId={`ai-settings-models-actions-${model.id}`}
          ariaLabel={`Open actions for ${model.name}`}
          items={[
            {
              label: 'Remove',
              testId: 'remove',
              icon: <Trash2 className="size-4" aria-hidden="true" />,
              destructive: true,
              disabled: Boolean(pendingModelIds[model.id]),
              disabledReason: 'This model is being updated.',
              onClick: () => setRemoving(model),
            },
          ]}
        />
      ),
    },
  ];

  return (
    <Card>
      <ListToolbar
        title="Models"
        description="Add the provider models this workspace can use, and set availability and per-token pricing for each."
        search={{
          value: q,
          onChange: list.setQ,
          placeholder: 'Search models…',
          testId: 'ai-settings-models-search',
        }}
        actions={
          <Button
            size="sm"
            startIcon={<Plus className="size-4" aria-hidden="true" />}
            onClick={() => setAdding(true)}
            testId="ai-settings-models-add"
          >
            Add model
          </Button>
        }
      />
      <CardBody>
        {alertMessage ? (
          <div className="mb-4" data-testid="ai-settings-models-alert">
            <Alert variant="error" title="Models not updated" message={alertMessage} />
          </div>
        ) : null}
        {isEmpty ? (
          <EmptyState
            icon={<Inbox className="size-8" aria-hidden="true" />}
            title="No models configured yet."
            description="Add a model to make it available."
            action={
              <Button
                size="sm"
                startIcon={<Plus className="size-4" aria-hidden="true" />}
                onClick={() => setAdding(true)}
                testId="ai-settings-models-empty-add"
              >
                Add model
              </Button>
            }
          />
        ) : (
          <PanelTable
            loading={isLoading}
            error={
              error && !data
                ? {
                    what: 'the models',
                    message: apiErrorMessage(error as ApiErrorInput),
                    onRetry: () => refetch(),
                  }
                : undefined
            }
            columns={columns}
            data={rows}
            getRowId={(model) => model.id}
            ariaLabel="Models"
            emptyMessage="No models match your search."
            emptyDescription="Clear the search or try another name."
            testId="ai-settings-models-table"
          />
        )}
      </CardBody>

      <AddModelsModal isOpen={adding} onClose={() => setAdding(false)} onDone={finishAdd} />

      <ConfirmModal
        isOpen={Boolean(removing)}
        onClose={() => setRemoving(null)}
        title={removing ? `Remove ${removing.name}?` : 'Remove model'}
        message="It will no longer be available to the AI features. You can add it again from the provider's list."
        confirmLabel="Remove"
        onConfirm={confirmRemove}
        testId="ai-settings-models-remove-modal"
      />

      <Modal
        isOpen={Boolean(inUse)}
        onClose={() => setInUse(null)}
        size="small"
        title={
          inUse
            ? `Can't ${inUse.action === 'remove' ? 'remove' : 'deactivate'} ${inUse.modelName}`
            : 'Model in use'
        }
        description={
          inUse?.action === 'remove'
            ? 'This model is still assigned to the items below, so it stays configured. Reassign them to a different model, then try again.'
            : 'This model is still assigned to the items below, so it stays active. Reassign them to a different model, then try again.'
        }
        testId="ai-settings-model-in-use"
        footer={
          <ModalActions
            onDismiss={() => setInUse(null)}
            dismissTestId="ai-settings-model-in-use-close"
          />
        }
      >
        {inUse ? (
          <div className="space-y-4">
            <UsageGroup label="Digital assistants" refs={inUse.digitalAssistants} />
            <UsageGroup label="Placements" refs={inUse.placements} />
            <UsageGroup label="AI settings" refs={inUse.configs} />
          </div>
        ) : null}
      </Modal>
    </Card>
  );
}
